import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import GeoResponsiveList from "../../common/Components/GeoList/GeoResponsiveList";
import notifications from "../../common/Components/Notifications/Notification";
import { canManageKarkhana } from "../../helpers/karkhanaEnabled";
import { createKarkhanaWorkType, listKarkhanaWorkTypes, updateKarkhanaWorkType } from "../../services/karkhanaApi";
import { geoRecordId } from "../gi-registry/geoPath";
import "../gi-registry/gi-registry.scss";
import PageHeader, { HeaderButton } from "./ui/PageHeader";
import { kp } from "./karkhanaPaths";

const readAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null") || {};
  } catch {
    return {};
  }
};

const KINDS: Record<string, string> = {
  SAREE: "Saree part",
  LEHENGA: "Lehenga",
  TAANI: "Taani",
  LOOM_PREP: "Loom preparation",
  DYEING: "Dyeing",
  OTHER: "Other",
};

type Form = { id?: string; code: string; name: string; category: string; defaultUnit: string; helperAllowed: boolean };

const EMPTY: Form = { code: "", name: "", category: "SAREE", defaultUnit: "INCH", helperAllowed: true };

/**
 * Saree parts and other work types: the codes rate cards pay by (S_1, S_3 anchal, D_1…). Studios can
 * add their own. A code never changes once made; the name changes only through Edit.
 */
const KarkhanaWorkTypesPage = () => {
  const history = useHistory();
  const role = readAuthUser()?.users?.role;
  const canWrite = canManageKarkhana(role) && role !== "SUPER_ADMIN";
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<any[]>([]);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [form, setForm] = useState<Form | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res: any = await listKarkhanaWorkTypes(0, 200);
      if (!res?.success) {
        setRows([]);
        setError(res?.message || "Unable to load saree parts");
        return;
      }
      setRows(Array.isArray(res?.data?.docs) ? res.data.docs : []);
    } catch {
      setRows([]);
      setError("Unable to load saree parts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canManageKarkhana(role)) {
      history.replace(kp.home());
      return;
    }
    load();
  }, [role, history, load]);

  const toggleActive = async (row: any) => {
    const id = geoRecordId(row);
    if (!id) return;
    setBusyId(id);
    try {
      const res: any = await updateKarkhanaWorkType(id, { active: !row.active });
      if (!res?.success) notifications({ title: "Error", message: res?.message || "Could not update", type: "danger" });
      await load();
    } finally {
      setBusyId("");
    }
  };

  const persist = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const res: any = form.id
        ? await updateKarkhanaWorkType(form.id, { name: form.name.trim(), helperAllowed: form.helperAllowed })
        : await createKarkhanaWorkType({ ...form, code: form.code.trim(), name: form.name.trim() });
      if (!res?.success) {
        notifications({ title: "Not saved", message: res?.message || "Could not save", type: "danger" });
        return;
      }
      notifications({ title: "Saved", message: `${res.data.code} · ${res.data.name}`, type: "success" });
      setForm(null);
      await load();
    } catch {
      notifications({ title: "No connection", message: "Nothing was saved. Try again.", type: "warning" });
    } finally {
      setSaving(false);
    }
  };

  if (!canManageKarkhana(role)) {
    return null;
  }

  return (
    <div className="gi-registry-page kw-page">
      <PageHeader title="Saree parts" back={kp.settings()}>
        {canWrite ? (
          <HeaderButton primary add onClick={() => setForm({ ...EMPTY })}>
            Add part
          </HeaderButton>
        ) : null}
      </PageHeader>
      <p className="gi-empty" style={{ paddingTop: 0 }}>
        The codes rate cards pay by. Add your own (e.g. D_1 for dyeing). A code never changes once made; use Edit to rename
        it, or switch it off when it's no longer used.
      </p>
      <GeoResponsiveList
        rows={rows}
        rowKey={(ele) => geoRecordId(ele) || ele.code}
        empty={loading ? "Loading parts…" : error || "No parts yet"}
        actions={(row) =>
          canWrite ? (
            <button
              type="button"
              className="kh-btn"
              onClick={() =>
                setForm({
                  id: geoRecordId(row),
                  code: row.code,
                  name: row.name || "",
                  category: row.category || "OTHER",
                  defaultUnit: row.defaultUnit || "INCH",
                  helperAllowed: !!row.helperAllowed,
                })
              }
            >
              Edit
            </button>
          ) : null
        }
        columns={[
          { key: "code", header: "Code", cell: (row) => <span className="kw-code">{row.code}</span> },
          { key: "name", header: "Name", cell: (row) => row.name },
          { key: "kind", header: "Kind", hideSm: true, cell: (row) => KINDS[row.category] || row.category },
          { key: "unit", header: "Unit", hideSm: true, cell: (row) => (row.defaultUnit === "INCH" ? "Inch" : "Piece") },
          { key: "helper", header: "Helper", hideSm: true, cell: (row) => (row.helperAllowed ? "Allowed" : "No") },
          {
            key: "active",
            header: "In use",
            cell: (row) =>
              canWrite ? (
                <label className="kh-switch" title={row.active ? "In use: tap to switch off" : "Off: tap to use again"}>
                  <input type="checkbox" checked={!!row.active} disabled={busyId === geoRecordId(row)} onChange={() => toggleActive(row)} />
                  <span aria-hidden="true" />
                  <span className="visually-hidden">{row.active ? "In use" : "Off"}</span>
                </label>
              ) : row.active ? (
                "In use"
              ) : (
                "Off"
              ),
          },
        ]}
      />

      <Modal isOpen={!!form} toggle={() => setForm(null)}>
        <ModalHeader toggle={() => setForm(null)}>{form?.id ? `Edit ${form.code}` : "Add part"}</ModalHeader>
        <ModalBody>
          {form ? (
            <>
              <Label>Code</Label>
              {form.id ? (
                <div className="kw-code" style={{ padding: "6px 0" }}>
                  {form.code} <span className="kw-muted">· fixed, rate cards use it</span>
                </div>
              ) : (
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. S_9 or D_1" autoFocus />
              )}
              <Label className="mt-2">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Border patti" autoFocus={!!form.id} />
              {!form.id ? (
                <>
                  <Label className="mt-2">Kind</Label>
                  <div className="kr-chips" role="radiogroup" aria-label="Kind">
                    {Object.entries(KINDS).map(([k, l]) => (
                      <button
                        key={k}
                        type="button"
                        role="radio"
                        aria-checked={form.category === k}
                        className={`kr-choice${form.category === k ? " is-on" : ""}`}
                        onClick={() =>
                          setForm({ ...form, category: k, defaultUnit: k === "SAREE" ? "INCH" : form.defaultUnit, helperAllowed: k === "SAREE" || k === "LEHENGA" })
                        }
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                  <Label className="mt-2">Measured in</Label>
                  <div className="kr-chips" role="radiogroup" aria-label="Measured in">
                    {[
                      ["INCH", "Inches"],
                      ["PIECE", "Pieces"],
                    ].map(([k, l]) => (
                      <button key={k} type="button" role="radio" aria-checked={form.defaultUnit === k} className={`kr-choice${form.defaultUnit === k ? " is-on" : ""}`} onClick={() => setForm({ ...form, defaultUnit: k })}>
                        {l}
                      </button>
                    ))}
                  </div>
                </>
              ) : null}
              <label className="mt-3" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="checkbox" checked={form.helperAllowed} onChange={(e) => setForm({ ...form, helperAllowed: e.target.checked })} />
                A helper can work on this
              </label>
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <button type="button" className="kh-btn" onClick={() => setForm(null)}>
            Cancel
          </button>
          <button type="button" className="kh-btn is-primary" disabled={saving || !form?.name.trim() || (!form?.id && !form?.code.trim())} onClick={persist}>
            {saving ? "Saving…" : "Save"}
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default KarkhanaWorkTypesPage;
